import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { base32Decode, base32Encode } from "./base32.js";
import { assertKeyLength, concatBytes, constantTimeEqual, utf8ToBytes, zeroBytes } from "./bytes.js";
import { CryptoError } from "./errors.js";
import { openSealed, openWithKey, sealTo, sealWithKey, sealWithKeyNonce } from "./team.js";
export const ACCOUNT_KEYS_AAD = utf8ToBytes("passwordflow/account-keys-v1");
const RECOVERY_WRAP_INFO = utf8ToBytes("passwordflow/recovery-wrap-v1");
const RECOVERY_AUTH_INFO = utf8ToBytes("passwordflow/recovery-auth-v1");
const PASSKEY_WRAP_INFO = utf8ToBytes("passwordflow/passkey-wrap-v1");
const EMPTY_SALT = new Uint8Array(0);
const KEY_BYTES = 32;
const RECOVERY_KEY_BYTES = 32;
const RECOVERY_CHECK_BYTES = 3;
const RECOVERY_GROUP = 4;
export function generateAccountKeys() {
    const vaultKey = new Uint8Array(KEY_BYTES);
    const wrapKey = new Uint8Array(KEY_BYTES);
    crypto.getRandomValues(vaultKey);
    crypto.getRandomValues(wrapKey);
    return { vaultKey, wrapKey };
}
/** Encrypts both account keys under a 32-byte key-encryption key. Output: nonce(24) | ciphertext. */
export function sealAccountKeys(kek, keys) {
    return sealAccountKeysWith(kek, keys, undefined);
}
/** Fixed-nonce form for the published test vectors. */
export function sealAccountKeysWith(kek, keys, nonce) {
    assertKeyLength(keys.vaultKey, KEY_BYTES, "VaultKey");
    assertKeyLength(keys.wrapKey, KEY_BYTES, "WrapKey");
    const plain = concatBytes(keys.vaultKey, keys.wrapKey);
    try {
        return nonce === undefined ? sealWithKey(kek, plain, ACCOUNT_KEYS_AAD) : sealWithKeyNonce(kek, nonce, plain, ACCOUNT_KEYS_AAD);
    }
    finally {
        zeroBytes(plain);
    }
}
export function openAccountKeys(kek, sealed) {
    const plain = openWithKey(kek, sealed, ACCOUNT_KEYS_AAD);
    if (plain.length !== KEY_BYTES * 2) {
        zeroBytes(plain);
        throw new CryptoError("invalid-input", "Account keys have the wrong length.");
    }
    const keys = { vaultKey: plain.slice(0, KEY_BYTES), wrapKey: plain.slice(KEY_BYTES) };
    zeroBytes(plain);
    return keys;
}
/**
 * Admin-assisted recovery: the account keys sealed to the instance recovery public key. Only an
 * admin holding the instance recovery secret can open it. Output: ephPub(32) | nonce(24) | ciphertext.
 */
export function sealAccountKeysTo(recipientPub, keys) {
    assertKeyLength(keys.vaultKey, KEY_BYTES, "VaultKey");
    assertKeyLength(keys.wrapKey, KEY_BYTES, "WrapKey");
    const plain = concatBytes(keys.vaultKey, keys.wrapKey);
    try {
        return sealTo(recipientPub, plain);
    }
    finally {
        zeroBytes(plain);
    }
}
export function openAccountKeysSealed(recipient, sealed) {
    const plain = openSealed(recipient, sealed);
    if (plain.length !== KEY_BYTES * 2) {
        zeroBytes(plain);
        throw new CryptoError("invalid-input", "Account keys have the wrong length.");
    }
    const keys = { vaultKey: plain.slice(0, KEY_BYTES), wrapKey: plain.slice(KEY_BYTES) };
    zeroBytes(plain);
    return keys;
}
/**
 * Passkey unlock: the input every client hands to the WebAuthn PRF extension. Fixed, so a passkey
 * yields the same secret on every device that syncs it.
 */
export const PASSKEY_PRF_SALT = sha256(utf8ToBytes("passwordflow/passkey-prf-v1"));
const PRF_OUTPUT_BYTES = 32;
/** The key that wraps the account keys for one passkey, from that passkey's PRF output. */
export function passkeyWrapKey(prfOutput) {
    assertKeyLength(prfOutput, PRF_OUTPUT_BYTES, "PRF output");
    return hkdf(sha256, prfOutput, EMPTY_SALT, PASSKEY_WRAP_INFO, KEY_BYTES);
}
/** 256 random bits. Shown to the user once, as text and QR code in the emergency kit. */
export function generateRecoveryKey() {
    const out = new Uint8Array(RECOVERY_KEY_BYTES);
    crypto.getRandomValues(out);
    return out;
}
export function recoveryKeys(recoveryKey) {
    assertKeyLength(recoveryKey, RECOVERY_KEY_BYTES, "RecoveryKey");
    return {
        wrapKey: hkdf(sha256, recoveryKey, EMPTY_SALT, RECOVERY_WRAP_INFO, KEY_BYTES),
        authKey: hkdf(sha256, recoveryKey, EMPTY_SALT, RECOVERY_AUTH_INFO, KEY_BYTES)
    };
}
function recoveryCheck(recoveryKey) {
    return sha256(recoveryKey).slice(0, RECOVERY_CHECK_BYTES);
}
/**
 * Human form: key plus a 3-byte checksum in base32, 56 characters in groups of four
 * ("ABCD-EFGH-…"). The checksum catches typing mistakes before any request is made.
 */
export function formatRecoveryKey(recoveryKey) {
    assertKeyLength(recoveryKey, RECOVERY_KEY_BYTES, "RecoveryKey");
    const text = base32Encode(concatBytes(recoveryKey, recoveryCheck(recoveryKey)));
    const groups = [];
    for (let i = 0; i < text.length; i += RECOVERY_GROUP) {
        groups.push(text.slice(i, i + RECOVERY_GROUP));
    }
    return groups.join("-");
}
/** Reads a typed or scanned recovery key. Ignores case, spaces and dashes; reads 0/1/8 as O/I/B. */
export function parseRecoveryKey(input) {
    const clean = input.toUpperCase().replace(/[\s-]/g, "").replace(/0/g, "O").replace(/1/g, "I").replace(/8/g, "B");
    if (clean.length !== 56) {
        throw new CryptoError("invalid-input", "Recovery key has the wrong length.");
    }
    const bytes = base32Decode(clean);
    if (bytes.length !== RECOVERY_KEY_BYTES + RECOVERY_CHECK_BYTES) {
        throw new CryptoError("invalid-input", "Recovery key has the wrong length.");
    }
    const key = bytes.slice(0, RECOVERY_KEY_BYTES);
    if (!constantTimeEqual(recoveryCheck(key), bytes.slice(RECOVERY_KEY_BYTES))) {
        zeroBytes(key);
        throw new CryptoError("invalid-input", "Recovery key checksum does not match.");
    }
    return key;
}
//# sourceMappingURL=accountKeys.js.map